import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField,
  Button,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { getInteractionTypes, getComponentNames, getUsersWithInteractions } from '../../api/userInteractionReports';

// Styles for the multi-select
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

/**
 * Component for filtering interaction data
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Function to call when filters change
 */
function InteractionFilters({ filters, onFilterChange }) {
  const [startDate, setStartDate] = useState(filters.startDate ? new Date(filters.startDate) : null);
  const [endDate, setEndDate] = useState(filters.endDate ? new Date(filters.endDate) : null);
  const [selectedTypes, setSelectedTypes] = useState(filters.interactionTypes || []);
  const [selectedUsers, setSelectedUsers] = useState(filters.userIds || []);
  const [selectedComponents, setSelectedComponents] = useState(filters.components || []);
  
  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load available filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load interaction types
        const types = await getInteractionTypes();
        setAvailableTypes(types);

        // Load component names
        const components = await getComponentNames();
        setAvailableComponents(components);

        // Load users
        const users = await getUsersWithInteractions();
        setAvailableUsers(users);
      } catch (error) {
        console.error('Error loading filter options:', error);
        setError('Failed to load filter options. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, []);

  // Apply filters
  const applyFilters = () => {
    const newFilters = {
      startDate: startDate ? startDate.toISOString().split('T')[0] : null,
      endDate: endDate ? endDate.toISOString().split('T')[0] : null,
      interactionTypes: selectedTypes,
      userIds: selectedUsers,
      components: selectedComponents
    };

    onFilterChange(newFilters);
  };

  // Reset filters
  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedTypes([]);
    setSelectedUsers([]);
    setSelectedComponents([]);

    onFilterChange({
      startDate: null,
      endDate: null,
      interactionTypes: [],
      userIds: [],
      components: []
    });
  };

  // Handle multi-select changes
  const handleTypeChange = (event) => {
    const value = event.target.value;
    setSelectedTypes(typeof value === 'string' ? value.split(',') : value);
  };

  const handleUserChange = (event) => {
    const value = event.target.value;
    setSelectedUsers(typeof value === 'string' ? value.split(',') : value);
  };

  const handleComponentChange = (event) => {
    const value = event.target.value;
    setSelectedComponents(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Filters
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* Date Range */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} sx={{ minWidth: 200 }} />}
                slotProps={{
                  textField: {
                    sx: { minWidth: 200 }
                  }
                }}
              />
              
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} sx={{ minWidth: 200 }} />}
                slotProps={{
                  textField: {
                    sx: { minWidth: 200 }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* Interaction Types */}
            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel id="interaction-type-label">Interaction Types</InputLabel>
              <Select
                labelId="interaction-type-label"
                id="interaction-type-select"
                multiple
                value={selectedTypes}
                onChange={handleTypeChange}
                input={<OutlinedInput label="Interaction Types" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {availableTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={selectedTypes.indexOf(type) > -1} />
                    <ListItemText primary={type} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Components */}
            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel id="component-label">Components</InputLabel>
              <Select
                labelId="component-label"
                id="component-select"
                multiple
                value={selectedComponents}
                onChange={handleComponentChange}
                input={<OutlinedInput label="Components" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {availableComponents.map((component) => (
                  <MenuItem key={component} value={component}>
                    <Checkbox checked={selectedComponents.indexOf(component) > -1} />
                    <ListItemText primary={component} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Users */}
            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel id="user-label">Users</InputLabel>
              <Select
                labelId="user-label"
                id="user-select"
                multiple
                value={selectedUsers}
                onChange={handleUserChange}
                input={<OutlinedInput label="Users" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const user = availableUsers.find(u => u.id === value);
                      return (
                        <Chip key={value} label={user ? user.email : value} />
                      );
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Checkbox checked={selectedUsers.indexOf(user.id) > -1} />
                    <ListItemText primary={user.email} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={resetFilters}>
              Reset Filters
            </Button>
            <Button variant="contained" onClick={applyFilters}>
              Apply Filters
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

export default InteractionFilters;
